/**
 * The planner's shared draft store. Later Steps extend this store (inputs,
 * resolved product, committed doors) rather than adding another one.
 *
 * The draft persists per tab, with a localStorage backup for reopening.
 * Unreadable saved data is discarded with a notice, and blocked storage falls
 * back to memory with a warning; neither ever throws into the UI.
 */
import {
  doorColours,
  doorPreviewColour,
  doorRules,
  doorTypeIds,
  type DoorColourName,
  type DoorTypeId,
  type MeasurementMode,
} from '@planner/config';
import { isValidReferenceCode, type Order } from '@planner/shared';
import type { LockSide } from '@planner/shared/doors';
import { create } from 'zustand';
import { persist, type PersistStorage } from 'zustand/middleware';
import { appConfig, region } from '../lib/env.js';

export type { MeasurementMode } from '@planner/config';

export interface DiyInputs {
  widthTop: string;
  widthMiddle: string;
  widthBottom: string;
  height: string;
  heightRight: string;
}

export interface TradeInputs {
  width: string;
  height: string;
}

export type AcknowledgementKind = 'width' | 'height' | 'depth';
export type GuardNotice = 'door-type' | 'measurements' | 'catalogue' | 'lock';
export type ReviewInput =
  'reviewLineId' | 'reviewDefaultName' | 'reviewName' | 'reviewQuantity' | 'editingLineId';

interface Acknowledgement {
  values: string[];
  rules: string;
}

export interface MeasurementsDraft {
  mode: MeasurementMode | null;
  diy: DiyInputs;
  trade: TradeInputs;
  depth: string;
  acknowledgements: Record<AcknowledgementKind, Acknowledgement | null>;
}

export interface LockDraft {
  height: string;
  side: LockSide | null;
}

export interface DoorDraft {
  doorType: DoorTypeId | null;
  /** Raw text entered on later Steps, kept as text so state never holds NaN. */
  inputs: Record<string, string>;
  measurements: MeasurementsDraft;
  lock: LockDraft;
  frameColour: DoorColourName;
  /** Catalogue product matched by later Steps. Derived, so never persisted. */
  resolved: { sku: string; unitPrice: number } | null;
}

export interface DoorState {
  draft: DoorDraft;
  cartReference: string | null;
  /** The API-confirmed Cart is reloaded from its reference after a refresh. */
  cart: Order | null;
  /** `unavailable` when the browser blocks storage; the draft is then memory-only. */
  persistence: 'ok' | 'unavailable';
  /** Describes discarded progress or a frame colour repaired during restoration. */
  restoreNotice: 'invalid' | 'unknown-colour' | 'invalid-cart' | null;
  guardNotice: GuardNotice | null;
  selectDoorType(doorType: DoorTypeId): void;
  selectMeasurementMode(mode: MeasurementMode): void;
  setDiyMeasurement(field: keyof DiyInputs, value: string): void;
  setTradeMeasurement(field: keyof TradeInputs, value: string): void;
  setDepth(value: string): void;
  setLockHeight(value: string): void;
  setLockSide(value: LockSide | null): void;
  selectFrameColour(frameColour: DoorColourName): void;
  initialiseReview(lineId: string, defaultName: string): void;
  setReviewInput(field: ReviewInput, value: string): void;
  setConfirmedCart(order: Order): void;
  clearCart(): void;
  acknowledge(kind: AcknowledgementKind): void;
  clearAcknowledgement(kind: AcknowledgementKind): void;
  setGuardNotice(notice: GuardNotice | null): void;
  resetDraft(): void;
  dismissRestoreNotice(): void;
}

interface SavedState {
  draft: Pick<DoorDraft, 'doorType' | 'inputs' | 'measurements' | 'lock' | 'frameColour'>;
  cartReference: string | null;
}

const version = 4;

function stateToSave(draft: DoorDraft, cartReference: string | null): SavedState {
  return {
    draft: {
      doorType: draft.doorType,
      inputs: draft.inputs,
      measurements: draft.measurements,
      lock: draft.lock,
      frameColour: draft.frameColour,
    },
    cartReference,
  };
}

function freshMeasurements(): MeasurementsDraft {
  return {
    mode: null,
    diy: { widthTop: '', widthMiddle: '', widthBottom: '', height: '', heightRight: '' },
    trade: { width: '', height: '' },
    depth: '',
    acknowledgements: { width: null, height: null, depth: null },
  };
}

function freshDraft(): DoorDraft {
  return {
    doorType: null,
    inputs: {},
    measurements: freshMeasurements(),
    lock: { height: '', side: null },
    frameColour: doorPreviewColour,
    resolved: null,
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isStringRecord(value: unknown): value is Record<string, string> {
  return isRecord(value) && Object.values(value).every((item) => typeof item === 'string');
}

function isAcknowledgement(value: unknown): value is Acknowledgement | null {
  return (
    value === null ||
    (isRecord(value) &&
      Array.isArray(value.values) &&
      value.values.every((item: unknown) => typeof item === 'string') &&
      typeof value.rules === 'string')
  );
}

function isMeasurementsDraft(value: unknown): value is MeasurementsDraft {
  if (
    !isRecord(value) ||
    !isRecord(value.diy) ||
    !isRecord(value.trade) ||
    !isRecord(value.acknowledgements)
  ) {
    return false;
  }
  const { diy, trade } = value;
  return (
    (value.mode === null || value.mode === 'diy' || value.mode === 'trade') &&
    ['widthTop', 'widthMiddle', 'widthBottom', 'height', 'heightRight'].every(
      (field) => typeof diy[field] === 'string',
    ) &&
    typeof trade.width === 'string' &&
    typeof trade.height === 'string' &&
    typeof value.depth === 'string' &&
    isAcknowledgement(value.acknowledgements.width) &&
    isAcknowledgement(value.acknowledgements.height) &&
    isAcknowledgement(value.acknowledgements.depth)
  );
}

function isLockDraft(value: unknown): value is LockDraft {
  return (
    isRecord(value) &&
    typeof value.height === 'string' &&
    (value.side === null || value.side === 'left' || value.side === 'right')
  );
}

function acknowledgementFor(
  measurements: MeasurementsDraft,
  kind: AcknowledgementKind,
): Acknowledgement {
  const { diy, depth } = measurements;
  switch (kind) {
    case 'width':
      return {
        values: [diy.widthTop, diy.widthMiddle, diy.widthBottom],
        rules: JSON.stringify([
          1,
          doorRules.clearance.width,
          doorRules.outOfSquareToleranceMm,
          '>=',
        ]),
      };
    case 'height':
      return {
        values: [diy.height, diy.heightRight],
        rules: JSON.stringify([
          1,
          doorRules.clearance.height,
          doorRules.heightSpreadToleranceMm,
          '>',
        ]),
      };
    case 'depth':
      return {
        values: [depth],
        rules: JSON.stringify([
          1,
          doorRules.depthClearance.min,
          doorRules.depthClearance.recommended,
          'min-inclusive',
        ]),
      };
  }
}

export function hasAcknowledgement(
  measurements: MeasurementsDraft,
  kind: AcknowledgementKind,
): boolean {
  const saved = measurements.acknowledgements[kind];
  if (!saved) {
    return false;
  }
  const current = acknowledgementFor(measurements, kind);
  return (
    saved.rules === current.rules &&
    saved.values.length === current.values.length &&
    saved.values.every((value, index) => value === current.values[index])
  );
}

/** Validate saved drafts and explicitly migrate earlier versions. */
function parseSaved(
  raw: string,
):
  | { state: SavedState; migrated: boolean; unknownColour: boolean; invalidCart: boolean }
  | undefined {
  let saved: unknown;
  try {
    saved = JSON.parse(raw);
  } catch {
    return undefined;
  }
  if (
    !isRecord(saved) ||
    (saved.version !== 1 &&
      saved.version !== 2 &&
      saved.version !== 3 &&
      saved.version !== version) ||
    !isRecord(saved.state)
  ) {
    return undefined;
  }
  const draft = saved.state.draft;
  if (!isRecord(draft)) {
    return undefined;
  }
  const { doorType, inputs } = draft;
  const validType = doorType === null ? null : doorTypeIds.find((id) => id === doorType);
  if (validType === undefined || !isStringRecord(inputs)) {
    return undefined;
  }
  const measurements =
    saved.version === 1
      ? freshMeasurements()
      : isMeasurementsDraft(draft.measurements)
        ? draft.measurements
        : undefined;
  if (!measurements) {
    return undefined;
  }
  const lock =
    saved.version === 3 || saved.version === version
      ? isLockDraft(draft.lock)
        ? draft.lock
        : undefined
      : { height: '', side: null };
  if (!lock) {
    return undefined;
  }
  const savedColour =
    saved.version === version
      ? doorColours.find((colour) => colour.name === draft.frameColour)?.name
      : undefined;
  const unknownColour = saved.version === version && !savedColour;
  const frameColour = savedColour ?? doorPreviewColour;
  const reference = saved.state.cartReference;
  const invalidCart =
    reference !== undefined &&
    reference !== null &&
    (typeof reference !== 'string' || !isValidReferenceCode(reference));
  const cartReference = typeof reference === 'string' && !invalidCart ? reference : null;
  const acknowledgements = {
    width: hasAcknowledgement(measurements, 'width') ? measurements.acknowledgements.width : null,
    height: hasAcknowledgement(measurements, 'height')
      ? measurements.acknowledgements.height
      : null,
    depth: hasAcknowledgement(measurements, 'depth') ? measurements.acknowledgements.depth : null,
  };
  return {
    state: {
      draft: {
        doorType: validType,
        inputs,
        measurements: { ...measurements, acknowledgements },
        lock,
        frameColour,
      },
      cartReference,
    },
    migrated: saved.version !== version || unknownColour || invalidCart,
    unknownColour,
    invalidCart,
  };
}

type DraftStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

export function createDoorStore(name: string, getStorage: () => DraftStorage) {
  // Problems found while reading storage, applied to state by `merge` below.
  let persistence: DoorState['persistence'] = 'ok';
  let restoreNotice: DoorState['restoreNotice'] = null;
  // Replaced once the store exists, so later write failures update state.
  let markUnavailable = () => {
    persistence = 'unavailable';
  };
  // Persist also calls setItem for transient notices; do not rewrite an unchanged draft.
  let lastSaved = JSON.stringify({ state: stateToSave(freshDraft(), null), version });

  const storage: PersistStorage<SavedState> = {
    getItem(key) {
      persistence = 'ok';
      restoreNotice = null;
      let saved: SavedState | undefined;
      try {
        const browserStorage = getStorage();
        const raw = browserStorage.getItem(key);
        if (raw !== null) {
          const parsed = parseSaved(raw);
          if (!parsed) {
            restoreNotice = 'invalid';
            browserStorage.removeItem(key);
          } else {
            saved = parsed.state;
            if (parsed.unknownColour) {
              restoreNotice = 'unknown-colour';
            } else if (parsed.invalidCart) {
              restoreNotice = 'invalid-cart';
            }
            lastSaved = JSON.stringify({ state: saved, version });
            if (parsed.migrated) {
              browserStorage.setItem(key, lastSaved);
            }
          }
        }
        // Probe a write so blocked storage is reported before the first choice.
        browserStorage.setItem(`${key}:probe`, '');
        browserStorage.removeItem(`${key}:probe`);
      } catch {
        persistence = 'unavailable';
      }
      return saved ? { state: saved, version } : null;
    },
    setItem(key, value) {
      const serialized = JSON.stringify(value);
      if (serialized === lastSaved) {
        return;
      }
      try {
        getStorage().setItem(key, serialized);
        lastSaved = serialized;
      } catch {
        markUnavailable();
      }
    },
    removeItem(key) {
      try {
        getStorage().removeItem(key);
      } catch {
        markUnavailable();
      }
    },
  };

  const store = create<DoorState>()(
    persist(
      (set) => ({
        draft: freshDraft(),
        cartReference: null,
        cart: null,
        persistence: 'ok',
        restoreNotice: null,
        guardNotice: null,
        selectDoorType: (doorType) =>
          set(({ draft }) => ({
            // A new type invalidates the matched product; later Steps resolve it again.
            draft: doorType === draft.doorType ? draft : { ...draft, doorType, resolved: null },
            restoreNotice: null,
            guardNotice: null,
          })),
        selectMeasurementMode: (mode) =>
          set(({ draft }) => ({
            draft:
              mode === draft.measurements.mode
                ? draft
                : { ...draft, measurements: { ...draft.measurements, mode }, resolved: null },
          })),
        setDiyMeasurement: (field, value) =>
          set(({ draft }) => {
            if (draft.measurements.diy[field] === value) {
              return { draft };
            }
            const kind = field.startsWith('width') ? 'width' : 'height';
            return {
              draft: {
                ...draft,
                measurements: {
                  ...draft.measurements,
                  diy: { ...draft.measurements.diy, [field]: value },
                  acknowledgements: { ...draft.measurements.acknowledgements, [kind]: null },
                },
                resolved: null,
              },
            };
          }),
        setTradeMeasurement: (field, value) =>
          set(({ draft }) => ({
            draft: {
              ...draft,
              measurements: {
                ...draft.measurements,
                trade: { ...draft.measurements.trade, [field]: value },
              },
              resolved: null,
            },
          })),
        setDepth: (depth) =>
          set(({ draft }) => ({
            draft:
              depth === draft.measurements.depth
                ? draft
                : {
                    ...draft,
                    measurements: {
                      ...draft.measurements,
                      depth,
                      acknowledgements: { ...draft.measurements.acknowledgements, depth: null },
                    },
                  },
          })),
        setLockHeight: (height) =>
          set(({ draft }) => ({
            draft:
              height === draft.lock.height ? draft : { ...draft, lock: { ...draft.lock, height } },
          })),
        setLockSide: (side) =>
          set(({ draft }) => ({
            draft: side === draft.lock.side ? draft : { ...draft, lock: { ...draft.lock, side } },
          })),
        selectFrameColour: (frameColour) =>
          set(({ draft }) => ({
            draft:
              frameColour === draft.frameColour ? draft : { ...draft, frameColour, resolved: null },
          })),
        initialiseReview: (lineId, defaultName) =>
          set(({ draft }) => ({
            draft: draft.inputs.reviewLineId
              ? draft
              : {
                  ...draft,
                  inputs: {
                    ...draft.inputs,
                    reviewLineId: lineId,
                    reviewDefaultName: defaultName,
                    reviewName: defaultName,
                    reviewQuantity: '1',
                  },
                },
          })),
        setReviewInput: (field, value) =>
          set(({ draft }) => ({
            draft: { ...draft, inputs: { ...draft.inputs, [field]: value } },
          })),
        setConfirmedCart: (cart) => set({ cart, cartReference: cart.referenceCode }),
        clearCart: () => set({ cart: null, cartReference: null }),
        acknowledge: (kind) =>
          set(({ draft }) => ({
            draft: {
              ...draft,
              measurements: {
                ...draft.measurements,
                acknowledgements: {
                  ...draft.measurements.acknowledgements,
                  [kind]: acknowledgementFor(draft.measurements, kind),
                },
              },
            },
          })),
        clearAcknowledgement: (kind) =>
          set(({ draft }) => ({
            draft: {
              ...draft,
              measurements: {
                ...draft.measurements,
                acknowledgements: { ...draft.measurements.acknowledgements, [kind]: null },
              },
              resolved: null,
            },
          })),
        setGuardNotice: (guardNotice) => set({ guardNotice }),
        resetDraft: () => set({ draft: freshDraft(), guardNotice: null }),
        dismissRestoreNotice: () => set({ restoreNotice: null }),
      }),
      {
        name,
        version,
        storage,
        partialize: ({ draft, cartReference }) => stateToSave(draft, cartReference),
        merge: (saved, current) => ({
          ...current,
          draft: saved ? { ...freshDraft(), ...(saved as SavedState).draft } : current.draft,
          cartReference: saved ? (saved as SavedState).cartReference : current.cartReference,
          persistence,
          restoreNotice,
        }),
      },
    ),
  );

  markUnavailable = () => {
    // The guard stops the failing write triggered by this update from looping.
    if (store.getState().persistence !== 'unavailable') {
      store.setState({ persistence: 'unavailable' });
    }
  };
  return store;
}

export const doorDraftStorageKey = `${appConfig.appSlug}-${region.code}`;

function tabDraftStorage(name: string): DraftStorage {
  const tab = window.sessionStorage;
  const shared = window.localStorage;
  return {
    getItem(key) {
      const current = tab.getItem(key);
      if (current !== null || key !== name) {
        return current;
      }
      const initial =
        shared.getItem(key) ?? JSON.stringify({ state: stateToSave(freshDraft(), null), version });
      tab.setItem(key, initial);
      return initial;
    },
    setItem(key, value) {
      tab.setItem(key, value);
      shared.setItem(key, value);
    },
    removeItem(key) {
      const current = tab.getItem(key);
      tab.removeItem(key);
      if (key !== name || shared.getItem(key) === current) {
        shared.removeItem(key);
      }
    },
  };
}

// Synchronous hydration keeps each tab's draft stable across refreshes.
export const useDoorStore = createDoorStore(doorDraftStorageKey, () =>
  tabDraftStorage(doorDraftStorageKey),
);
